<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentCard from 'dashboard/components-next/aifocus/AgentCard.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const agents = useMapGetter('aifocusAgents/getRecords');

const isFetching = computed(() => uiFlags.value.fetchingList);
const syncingAgentId = ref(null);
const deletingAgentId = ref(null);
const isSyncingAll = ref(false);

const accountId = computed(() => route.params.accountId);

onMounted(() => {
  store.dispatch('aifocusAgents/get');
});

const handleCreate = () => {
  router.push({
    name: 'aifocus_agents_new',
    params: { accountId: accountId.value },
  });
};

const handleCardClick = agent => {
  router.push({
    name: 'aifocus_agents_edit',
    params: { accountId: accountId.value, agentId: agent.id },
  });
};

const handleSync = async agent => {
  try {
    syncingAgentId.value = agent.id;
    await store.dispatch('aifocusAgents/sync', { id: agent.id });
    useAlert('Agente sincronizado com IA');
    await store.dispatch('aifocusAgents/get');
  } catch (error) {
    useAlert(error?.message || 'Erro ao sincronizar agente');
  } finally {
    syncingAgentId.value = null;
  }
};

const handleDelete = async agent => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(`Tem certeza que deseja excluir o agente "${agent.name}"?`)) {
    return;
  }
  try {
    deletingAgentId.value = agent.id;
    await store.dispatch('aifocusAgents/delete', agent.id);
    useAlert('Agente excluido com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao excluir agente');
  } finally {
    deletingAgentId.value = null;
  }
};

const handlePlayground = agent => {
  router.push({
    name: 'aifocus_agents_playground_index',
    params: { accountId: accountId.value, agentId: agent.id },
  });
};

const handleSyncAll = async () => {
  try {
    isSyncingAll.value = true;
    const result = await store.dispatch('aifocusAgents/syncAll');
    const { created, updated, errors } = result;

    let message = 'Sincronizacao concluida! ';
    if (created > 0) message += `${created} agente(s) criado(s). `;
    if (updated > 0) message += `${updated} agente(s) atualizado(s). `;
    if (errors > 0) message += `${errors} erro(s).`;

    useAlert(message);
    await store.dispatch('aifocusAgents/get');
  } catch (error) {
    useAlert(error?.message || 'Erro ao sincronizar agentes');
  } finally {
    isSyncingAll.value = false;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #header>
      <div class="flex items-center justify-between w-full px-6 py-4">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">IA Agents</h1>
          <p class="text-sm text-n-slate-11">
            Gerencie seus agentes de IA integrados com IA
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            label="Sincronizar Agentes"
            icon="i-lucide-refresh-cw"
            variant="secondary"
            :is-loading="isSyncingAll"
            @click="handleSyncAll"
          />
          <Button
            label="Criar Agente"
            icon="i-lucide-plus"
            @click="handleCreate"
          />
        </div>
      </div>
    </template>

    <template #body>
      <div class="px-6 pb-6">
        <!-- Empty State -->
        <div
          v-if="!isFetching && agents.length === 0"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div
            class="w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-n-brand/20 to-n-brand/5 flex items-center justify-center"
          >
            <span class="i-lucide-bot text-4xl text-n-brand" />
          </div>
          <h3 class="text-xl font-semibold text-n-slate-12 mb-2">
            Nenhum agente criado
          </h3>
          <p class="text-sm text-n-slate-11 mb-8 max-w-md">
            Crie seu primeiro agente de IA para automatizar conversas e
            atendimentos. Os agentes podem responder mensagens, qualificar leads
            e muito mais.
          </p>
          <Button
            label="Criar Primeiro Agente"
            icon="i-lucide-plus"
            size="lg"
            @click="handleCreate"
          />
        </div>

        <!-- Agents Grid -->
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <AgentCard
            v-for="agent in agents"
            :key="agent.id"
            :agent="agent"
            :is-loading="syncingAgentId === agent.id"
            @click="handleCardClick"
            @sync="handleSync"
            @delete="handleDelete"
            @playground="handlePlayground"
          />

          <!-- Create New Card -->
          <div
            class="border-2 border-dashed border-n-weak rounded-2xl p-5 flex flex-col items-center justify-center min-h-[200px] hover:border-n-brand hover:bg-n-alpha-1 transition-all cursor-pointer group"
            @click="handleCreate"
          >
            <div
              class="w-14 h-14 rounded-xl bg-n-alpha-2 group-hover:bg-n-brand/10 flex items-center justify-center mb-4 transition-colors"
            >
              <span
                class="i-lucide-plus text-2xl text-n-slate-10 group-hover:text-n-brand transition-colors"
              />
            </div>
            <span
              class="text-sm font-medium text-n-slate-11 group-hover:text-n-brand transition-colors"
            >
              Criar Novo Agente
            </span>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
